import {afterNextRender, computed, DestroyRef, inject, Injectable, OnInit, PLATFORM_ID, signal} from '@angular/core';
import {CookieConsentService} from '../../component/cookie-consent/cookie-consent.service';
import {environment} from '../../../environments/environment';
import {CookieConsentModel} from '../../enum/cookie-consent.enum';
import {AnalyticsEvent, ConsentStatus, ConsentStorageType} from '../../enum/gtm-cookie-consent.enum';
import {isPlatformBrowser} from '@angular/common';
import {skipUntil, Subject, take} from 'rxjs';
import {googleAnalyticsDebugSignal} from '../../signal/common/analytics/google-analytics-debug.signal';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService implements OnInit {
  private readonly cookieConsentService: CookieConsentService = inject(CookieConsentService);
  private readonly GTM_ID = environment.analytics.google.gtmContainerId;
  private readonly areAnalyticsEnabled: boolean = environment.analytics.google.enabled;
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly initialized = signal(false);
  private eventQueueSubject = new Subject<AnalyticsEvent>();
  private readonly eventQueue$ = this.eventQueueSubject.asObservable();

  private canProcessEvents$ = toObservable(computed(() =>
    this.isBrowser &&
    this.areAnalyticsEnabled &&
    this.initialized() &&
    CookieConsentModel.All === this.cookieConsentService.cookieConsentSignal()
  ));

  constructor(
    private readonly destroyRef: DestroyRef
  ) {
  }

  ngOnInit(): void {
    afterNextRender(() => {
      this.initializeEventProcessing();
    });
  }

  public initGtm(): void {
    if (!this.isBrowser || this.initialized()) return;
    this.injectGtmSnippet();
    this.setupAnalyticsDebug();
    this.initialized.set(true);
  }

  public updateConsent(status: ConsentStatus): void {
    if (!this.isBrowser || !this.initialized()) return;

    (window as any).gtag?.('consent', 'update', {
      [ConsentStorageType.Ad]: status,
      [ConsentStorageType.Analytics]: status,
      [ConsentStorageType.Personalization]: status,
      [ConsentStorageType.AdUserData]: status,
      [ConsentStorageType.AdPersonalization]: status,
    });
  }

  public trackEvent(event: AnalyticsEvent): void {
    this.eventQueueSubject.next(event);
  }

  private initializeEventProcessing(): void {
    this.eventQueue$
      .pipe(
        skipUntil(
          this.canProcessEvents$.pipe(
            filter(isAllowed => isAllowed),
            take(1)
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => this.handleEvent(event));
  }

  private handleEvent(event: AnalyticsEvent): void {
    (window as any).gtag?.('event', event.event, {
      event_category: event.eventCategory,
      value: event.eventPayload ?? null,
    });
  }

  private injectGtmSnippet(): void {
    this.removeGTM();

    (window as any).dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});

    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.GTM_ID}`;
    document.head.insertBefore(script, document.head.firstChild);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${this.GTM_ID}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  private removeGTM(): void {
    if (!this.isBrowser) return;

    document.getElementById('gtm-script')?.remove();
    document.querySelectorAll('noscript > iframe[src*="googletagmanager.com/ns.html"]').forEach(element => element.parentElement?.remove());
  }

  public setupAnalyticsDebug(): void {
    googleAnalyticsDebugSignal.set(environment.analytics.google.debug ?? false);
  }
}
